export default function PHPVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - PHPVEdit
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for PHPVEdit.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I write and run PHP?
              </h3>
              <p className="mt-1">
                The app opens directly into the editor. Write your PHP, tap
                Run, and the output appears in the panel below. Compilation
                and execution happen entirely on your device using the
                built-in PHP runtime — no internet connection is required.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What PHP version does the app use?
              </h3>
              <p className="mt-1">
                PHPVEdit ships with a modern PHP 8.x runtime. Modern language
                features such as arrow functions, named arguments, match
                expressions, null-safe operators, and readonly properties
                all work out of the box.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What are the runnable recipes?
              </h3>
              <p className="mt-1">
                The cheat-sheet drawer contains a curated library of short,
                runnable PHP recipes grouped by topic (arrays, strings,
                regex, date-time, HTTP, JSON, files, functions, OOP, and
                more). Tap any recipe to load it into the editor, run it,
                and then remix it.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I send input to a program that reads{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  fgets(STDIN)
                </code>
                ?
              </h3>
              <p className="mt-1">
                Open the Input panel before tapping Run and type or paste
                what your program should read. When the program reads, it
                consumes from that buffer.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does the Modernize helper do?
              </h3>
              <p className="mt-1">
                Modernize scans your snippet for legacy patterns
                (<code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">mysql_*</code>
                {" "}calls,{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">array(...)</code>
                {" "}literals,{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">isset</code>
                {" "}ternaries, short tags, untyped parameters) and proposes
                a rewrite in modern PHP. You can apply the in-place rewrites
                with one tap or review the suggestions one by one.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save a snippet?
              </h3>
              <p className="mt-1">
                Tap the save icon in the editor toolbar. Snippets live in
                your local library with optional tags; the most recent 20
                show up at the top of the drawer for quick access.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why do some PHP extensions or functions not work?
              </h3>
              <p className="mt-1">
                The bundled runtime ships with common PHP features but does
                not include every extension you might find on a full server
                install. Database drivers, full stream wrappers for remote
                URLs, and some system-level extensions may be missing. If a
                function you need is not available, email us.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why do I see a message that the offline runtime is being
                prepared?
              </h3>
              <p className="mt-1">
                The native runtime is bundled as a separate package. In a
                fresh install, the runtime may initialise on first launch.
                If the message persists, open Settings &gt; About and check
                the version — we will ship a fixed build quickly.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with an optional one-time in-app purchase from Settings.
                No subscriptions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                My program hangs when I tap Run. What should I do?
              </h3>
              <p className="mt-1">
                Tap Stop to cancel execution. Check for infinite loops, or
                for a read from standard input when the Input panel is
                empty. If the editor still feels stuck, close and reopen the
                snippet.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Close and reopen the app. Make sure you have the latest
                version from Google Play. If a specific snippet causes the
                crash, check for very large arrays, infinite recursion, or
                very long loops. Email us with your device model, OS
                version, app version, and steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details
            in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
            <li>
              If it is a runtime error question, the shortest PHP snippet
              that reproduces it
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/phpvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/phpvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/phpvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/phpvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> PHPVEdit
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.phpvedit
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
